"use client"

import { useEffect, useState } from 'react'
import { Bell, BellRing, Clock, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useNotificationStore } from '@/stores/notification-store'
import { useAuthStore } from '@/stores'
import { useProfileStore } from '@/stores/profile-store'
import { useComponentsTranslations } from '@/hooks/use-translations'

/**
 * Shows a one-time Dialog prompting the user to enable browser notifications.
 * Explains why permissions are needed and remembers the user's choice in the profile table.
 * Only shown when permission is 'default' (not yet decided).
 */
export function NotificationPermissionPrompt() {
  const t = useComponentsTranslations()
  const user = useAuthStore((s) => s.user)
  const browserPermission = useNotificationStore((s) => s.browserPermission)
  const requestPermission = useNotificationStore((s) => s.requestPermission)
  const refreshPermissionState = useNotificationStore((s) => s.refreshPermissionState)
  const markAsSeen = useNotificationStore((s) => s.markPermissionPromptAsSeen)
  const hasSeen = useProfileStore((s) => s.profile?.has_seen_notification_prompt === true)
  const updateProfile = useProfileStore((s) => s.updateProfile)

  // Start closed to avoid a flash before profile is loaded
  const [open, setOpen] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  useEffect(() => {
    if (!user) return
    refreshPermissionState()

    if (!hasSeen) {
      // Small delay so it doesn't pop instantly on page load
      const timer = setTimeout(() => setOpen(true), 1200)
      return () => clearTimeout(timer)
    }
  }, [user, hasSeen, refreshPermissionState])

  // Close immediately if permission was already decided outside this dialog
  useEffect(() => {
    if (browserPermission !== 'default') {
      setOpen(false)
    }
  }, [browserPermission])

  const markDismissed = () => {
    updateProfile({ has_seen_notification_prompt: true }).catch((err) =>
      console.error('[NotificationPrompt] Failed to save dismissed state:', err)
    )
    markAsSeen()
  }

  const handleEnable = async () => {
    setIsRequesting(true)
    await requestPermission()
    setIsRequesting(false)
    setOpen(false)
    markDismissed()
  }

  const handleMaybeLater = () => {
    setOpen(false)
    markDismissed()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleMaybeLater() }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mx-auto">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-lg">{t('notif_prompt_title')}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t('notif_prompt_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
            <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">{t('notif_prompt_feature_timer_title')}</p>
              <p className="text-xs text-muted-foreground">{t('notif_prompt_feature_timer_body')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
            <BellRing className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">{t('notif_prompt_feature_reminders_title')}</p>
              <p className="text-xs text-muted-foreground">{t('notif_prompt_feature_reminders_body')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">{t('notif_prompt_feature_control_title')}</p>
              <p className="text-xs text-muted-foreground">{t('notif_prompt_feature_control_body')}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            className="w-full"
            onClick={handleEnable}
            disabled={isRequesting}
          >
            {isRequesting ? t('notif_prompt_requesting') : t('notif_prompt_enable')}
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={handleMaybeLater}
            disabled={isRequesting}
          >
            {t('notif_prompt_maybe_later')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
